<template>
  <div>
    <v-toolbar flat color="transparent">
      <AddButton
        v-if="canEdit"
        :advertiser="advertiser"
        :sales-account-list="salesAccountList"
        :loading="loading"
        @openCampaignModal="openCampaignModal"
      />
      <span class="checkboxDescription">表示する案件：</span>
      <div>
        <v-checkbox
          :key="index"
          v-for="(option, index) in selectOptions"
          :label="option.text"
          @change="onActiveFilterChange(option.value)"
          :input-value="activeFilter[option.value]"
          class="checkbox"
          hide-details
          messages="none"
        />
      </div>
      <v-spacer />
      <InsertionOrdersSearchBox @onTextChange="onTextChange" />
      <PageSelect
        :value="searchResult"
        @input="pagination"
        style="width:160px;"
        class="pl-4"
      />
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="items"
      :expand="keepExpand"
      :disable-initial-sort="true"
      :rows-per-page-items="[-1]"
      hide-actions
      class="elevation-1"
    >
      <template #headers="props">
        <th
          v-for="header in props.headers"
          :key="header.index"
          :class="[
            'column sortable px-2 py-0',
            sortOptions.descending ? 'desc' : 'asc',
            header.value === sortOptions.sortBy ? 'active' : '',
            header.align === 'center' ? 'text-xs-center' : '',
            header.align === 'right' ? 'text-xs-right' : '',
            header.align === 'left' ? 'text-xs-left' : '',
          ]"
          :aria-sort="header.sortable ? '' : 'none'"
          @click="changeSort(header)"
        >
          {{ header.text }}
          <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
        </th>
      </template>

      <template v-slot:items="props">
        <tr
          :key="props.index"
          :class="[props.expanded ? 'opened-io-row' : 'io-row']"
        >
          <td
            v-bind:style="targetStyle(props.item.id)"
            @click="props.expanded = !props.expanded"
            class="io-row text-xs-center expandColumn"
          >
            <v-btn icon>
              <v-icon :class="{ iconExpanded: props.expanded }">
                keyboard_arrow_down
              </v-icon>
            </v-btn>
          </td>
          <td
            v-bind:style="targetStyle(props.item.id)"
            class="io-row text-xs-left pa-0"
          >
            <StatusSwitch
              :insertion-order="props.item"
              :status="props.item.status === 'active'"
              :disabled="!canEditItem(props.item)"
            />
          </td>
          <td
            v-bind:style="targetStyle(props.item.id)"
            class="io-row text-xs-left no-wrap pa-0"
          >
            {{ props.item.anIoId }}
          </td>
          <td
            v-bind:style="targetStyle(props.item.id)"
            class="io-row text-xs-left no-wrap"
          >
            {{ props.item.name }}
          </td>
          <td
            v-bind:style="targetStyle(props.item.id)"
            class="io-row text-xs-center no-wrap"
          >
            <v-btn
              v-if="canEditItem(props.item)"
              @click="openCampaignModal(props.item)"
              small
            >
              <v-icon>add</v-icon>キャンペーン追加
            </v-btn>
          </td>
          <td
            v-bind:style="targetStyle(props.item.id)"
            class="io-row justify-left align-center layout px-0"
          >
            <EditButton
              v-if="canShowEditButton(props.item)"
              :can-edit="canEditItem(props.item)"
              :permission="props.item.permission"
              @click="openEditModal(props.item)"
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  @click="openIntervalReportsWindow(props.item.id)"
                  icon
                  small
                >
                  <v-icon>email</v-icon>
                </v-btn>
              </template>
              <span>レポートメール定期配信設定</span>
            </v-tooltip>
            <DeleteButton
              v-if="!props.item.isActive && canEditItem(props.item)"
              :insertion-order="props.item"
            />
          </td>
        </tr>
      </template>

      <template v-slot:expand="props">
        <CampaignTable
          :insertion-order="props.item"
          :system-owner="systemOwner"
          :can-edit="canEditItem(props.item)"
          :last-modified-timestamp="campaignLastModifiedTimestamp"
          @refresh="updateTimestamp"
          @edit="openCampaignModalEdit"
          @duplicate="openCampaignModalDuplicate"
        />
        <v-divider />
      </template>

      <template slot="no-data">
        <template v-if="loading.search">
          <v-btn loading flat class="loading" />
        </template>
        <template v-if="!loading.search">
          <div>登録されている案件はありません</div>
        </template>
      </template>
    </v-data-table>
    <IOModalEdit
      v-if="focusedItem && modalVisible"
      v-model="modalVisible"
      :advertiser="advertiser"
      :can-edit="canEditItem(focusedItem)"
      :insertion-order="focusedItem"
      :sales-account-list="salesAccountList"
      :loading="loading"
      @success="onSuccess"
      @cancel="onCancel"
    />
    <CampaignModal
      v-if="focusedItem && showCampaignModal"
      v-model="showCampaignModal"
      :insertion-order="focusedItem"
      :advertiser="advertiser"
      :campaign-summary="focusedCampaign"
      :source-campaign="sourceCampaign"
      :inventory-lists="inventoryLists"
      :system-owner="systemOwner"
      @editInsertionOrder="openEditModal(focusedItem)"
      @save="updateTimestamp"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import IOModalEdit from './IOModalEdit.vue'
import CampaignModal from './CampaignModal.vue'
import CampaignTable from './CampaignTable.vue'
import AddButton from './Button/AddButton.vue'
import StatusSwitch from './StatusSwitch'
import DeleteButton from './Button/DeleteButton'
import EditButton from './Button/EditButton'
import InsertionOrdersSearchBox from './IOSearchBox'
import PageSelect from '@/components/Shared/Search/PageSelect'
import advertiserApi from '@/api/AdvertiserApi'

export default {
  name: 'IOTable',
  components: {
    StatusSwitch,
    DeleteButton,
    EditButton,
    AddButton,
    CampaignTable,
    CampaignModal,
    IOModalEdit,
    InsertionOrdersSearchBox,
    PageSelect,
  },
  props: {
    advertiser: {
      type: Object,
      default: () => {},
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Object,
      default: () => {},
    },
    salesAccountList: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    inventoryLists: {
      type: Array,
      default: () => [],
    },
    systemOwner: {
      type: Object,
      required: true,
    },
    isAdvertiser: {
      type: Boolean,
    },
  },
  data() {
    return {
      headers: [
        {
          text: '',
          value: 'openCampaignTableButton',
          align: 'left',
          sortable: false,
        },
        {
          text: 'ステータス',
          value: 'status',
          align: 'left',
          width: '120',
          class: 'pa-0',
        },
        {
          text: 'id',
          value: 'id',
          align: 'left',
          sortable: true,
          width: '100',
          class: 'pa-0',
        },
        { text: '案件名', value: 'name', align: 'left', sortable: true },
        {
          text: '',
          value: 'addCampaignButton',
          align: 'left',
          sortable: false,
        },
        { text: '編集/削除', sortable: false, align: 'left' },
      ],
      modalVisible: false,
      modalCampaignVisible: false,
      focusedItem: null,
      focusedCampaign: null,
      sourceCampaign: null,
      isValid: false,
      keepExpand: false,
      isNew: false,
      activeFilter: { active: true, inactive: true },
      campaignLastModifiedTimestamp: Date.now(),
    }
  },
  computed: {
    ...mapGetters('advertiser/insertionOrders', [
      'keyword',
      'advertiserId',
      'pageNo',
      'sortOptions',
      'searchResult',
    ]),
    focusedCampaignId() {
      if (this.focusedCampaign) return this.focusedCampaign.id
      return 0
    },
    advertiserId() {
      return this.$route.params.advertiserId
    },
    editModalTitle() {
      return this.focusedItem && this.focusedItem.name
    },
    showCampaignModal: {
      get() {
        return this.modalCampaignVisible
      },
      set(value) {
        if (!value) {
          this.sourceCampaign = null
          this.focusedCampaign = null
        }
        this.modalCampaignVisible = value
      },
    },
    selectOptions() {
      return [
        { text: '有効', value: 'active' },
        { text: '無効', value: 'inactive' },
      ]
    },
  },
  created() {
    this.initSearch()
  },
  methods: {
    ...mapActions('advertiser/insertionOrders', {
      initSearchItems: 'INIT_SEARCH_ITEMS',
      setSearchKeyword: 'SET_SEARCH_KEYWORD',
      setPageNo: 'SET_SEARCH_PAGE_NO',
      setStatuses: 'SET_SEARCH_STATUSES',
      updateSortOptions: 'UPDATE_SORT_OPTIONS',
      setInsertionOrders: 'SET_INSERTION_ORDERS',
    }),
    updateInsertionOrders() {
      this.setInsertionOrders({
        keyword: this.keyword,
        advertiserId: this.advertiserId,
        pageNo: this.pageNo,
        sortOptions: this.sortOptions,
      })
    },
    initSearch() {
      this.initSearchItems()
      this.updateInsertionOrders()
    },
    onTextChange(searchWord) {
      this.setSearchKeyword(searchWord)
      this.setPageNo(1)
      this.updateInsertionOrders()
    },
    pagination(pageNo) {
      this.setPageNo(pageNo)
      this.updateInsertionOrders()
    },
    onActiveFilterChange(status) {
      const state = !this.activeFilter[status]
      this.activeFilter[status] = state
      this.setStatuses(this.getFilteredStatuses())
      this.setPageNo(1)
      this.updateInsertionOrders()
    },
    getFilteredStatuses() {
      const active = this.activeFilter.active
      const inactive = this.activeFilter.inactive
      if (active && !inactive) {
        return ['active']
      } else if (!active && inactive) {
        return ['inactive']
      } else if (!active && !inactive) {
        return null
      } else {
        return ['active', 'inactive']
      }
    },
    changeSort(header) {
      if (!header.sortable) return
      const column = header.value
      const newSortOptions = {
        ...this.sortOptions,
      }
      if (newSortOptions.sortBy === column) {
        newSortOptions.descending = !newSortOptions.descending
      } else {
        newSortOptions.sortBy = column
        newSortOptions.descending = false
      }
      this.sortOptions.sortBy = newSortOptions.sortBy
      this.sortOptions.descending = newSortOptions.descending
      this.updateSortOptions(newSortOptions)
      this.updateInsertionOrders()
    },
    openEditModal(insertionOrder) {
      this.focusedItem = insertionOrder
      this.modalVisible = true
    },
    onSuccess() {
      this.focusedItem = null
      this.modalVisible = false
    },
    onCancel() {
      this.focusedItem = null
      this.modalVisible = false
    },
    openCampaignModal(insertionOrder) {
      this.isNew = true
      this.focusedItem = insertionOrder
      this.focusedCampaign = null
      this.modalCampaignVisible = true
    },
    openCampaignModalEdit(event) {
      this.isNew = false
      this.focusedItem = event.insertionOrder
      this.focusedCampaign = event.item
      this.modalCampaignVisible = true
    },
    openCampaignModalDuplicate(event) {
      this.isNew = true
      this.focusedItem = event.insertionOrder
      this.sourceCampaign = event.item
      this.modalCampaignVisible = true
    },
    /**
     * このボタンは下記条件を満たす場合見れなくなる。
     * - 広告主でログインしている
     * - かつ、案件 permission が readonly
     */
    canShowEditButton(insertionOrder) {
      return !(insertionOrder.permission === 'readonly' && this.isAdvertiser)
    },
    /**
     * 編集権限があるかどうか
     */
    canEditItem(insertionOrder) {
      return insertionOrder.isEditable
    },
    /**
     * URLパラメーターのidが同一の場合
     * 背景強調のスタイルを返却する
     * @param id 案件ID
     */
    targetStyle(id) {
      if (parseInt(this.$route.query.id) === id) {
        return 'background-color: #ccffff;'
      } else {
        return ''
      }
    },
    updateTimestamp() {
      this.campaignLastModifiedTimestamp = Date.now()
    },
    async getAgencyId(advertiserId) {
      const res = await advertiserApi.fetchAdvertiser(advertiserId)
      return res.item.agencyId
    },
    async openIntervalReportsWindow(insertionOrderId) {
      const source = 'insertionOrderList'
      const routerParams = {
        name: 'Report Interval Mail',
        query: {
          insertionOrderId: insertionOrderId,
          source: source,
        },
      }
      this.$sendEventToGA('buttonClick', {
        buttonName: 'intervalReportSettingModalOpenButton',
        source: source,
      })
      const resolvedRoute = this.$router.resolve(routerParams)
      window.open(resolvedRoute.href, '_blank', 'noreferrer')
    },
  },
}
</script>

<style scoped lang="scss">
.title {
  flex: 1;
}
.search {
  max-width: 250px;
}
.loading {
  width: 100%;
}
.expandColumn {
  width: 60px;
}
.iconExpanded {
  transform: rotate(-180deg);
}

.theme--light.v-table tbody tr:hover:not(.v-datatable__expand-row) {
  background: #cce5ff;
}

tr.io-row {
  background-color: #f9f9f9;
  min-height: 52px;
}
tr.opened-io-row {
  background-color: #cce5ff;
}

.checkboxDescription {
  width: 115px;
  padding-top: 24px;
  padding-bottom: 20px;
  font-size: 16px;
  color: #666;
}
.checkbox {
  padding-top: 8px;
  display: inline-block;
  margin-right: 15px;
}
</style>
